/**
 * UTC month window helpers for compact (mobile/tablet) hourly chart rendering.
 * Full-year data stays in state; only the visible month is materialized for Highcharts.
 */

/** @param {number} year @param {number} monthIndex 0–11 */
export function getMonthHourBounds(year, monthIndex) {
  const yearStartMs = Date.UTC(year, 0, 1);
  const startMs = Date.UTC(year, monthIndex, 1);
  const endMsExclusive = Date.UTC(year, monthIndex + 1, 1);
  const startHour = Math.floor((startMs - yearStartMs) / 3600000);
  const endHour = Math.floor((endMsExclusive - yearStartMs) / 3600000);
  return {
    startHour,
    endHour,
    startMs,
    endMsExclusive,
    endMs: endMsExclusive - 3600000,
  };
}

/** @param {number} year @param {number} timestampMs */
export function getUtcMonthIndexForYear(year, timestampMs) {
  const yearStart = Date.UTC(year, 0, 1);
  const yearEndExclusive = Date.UTC(year + 1, 0, 1);
  if (timestampMs < yearStart) return 0;
  if (timestampMs >= yearEndExclusive) return 11;
  for (let m = 11; m >= 0; m -= 1) {
    if (timestampMs >= Date.UTC(year, m, 1)) return m;
  }
  return 0;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** @param {number} year @param {number} monthIndex 0–11 */
export function formatHourlyChartMonthLabel(year, monthIndex) {
  const name = MONTH_NAMES[monthIndex] ?? 'Month';
  return `${name} ${year}`;
}

/**
 * Build [timestamp, value] pairs for one UTC month from an hourly profile array.
 * @param {number[]|null|undefined} profileData
 * @param {number} year
 * @param {number} monthIndex 0–11
 * @param {(value: number, hourIndex: number) => number} mapValue
 * @returns {[number, number][]|null}
 */
export function buildHourlyTimeSeriesMonthSlice(
  profileData,
  year,
  monthIndex,
  mapValue = (v) => v / 1000
) {
  if (!profileData || !Array.isArray(profileData) || profileData.length === 0) {
    return null;
  }
  const { startHour, endHour } = getMonthHourBounds(year, monthIndex);
  const yearStartMs = Date.UTC(year, 0, 1);
  const points = [];
  const limit = Math.min(endHour, profileData.length);
  for (let i = startHour; i < limit; i += 1) {
    points.push([yearStartMs + i * 3600000, mapValue(profileData[i], i)]);
  }
  return points.length > 0 ? points : null;
}
